import * as React from "react";
import { ChartCard } from "./chart-card";
import { CHART_COLORS } from "./chart-theme";

export interface FunnelStage {
  label: string;
  value: number;
}

export interface FunnelBarsProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  /** Ordered stages, widest (top of funnel) first. */
  stages: FunnelStage[];
  className?: string;
}

/**
 * Vertical funnel: each stage is a full-width row whose fill is proportional to
 * the first (top) stage, with a step-over conversion % vs the previous stage.
 * Dependency-free so it never competes with Recharts for layout.
 */
export function FunnelBars({
  title,
  subtitle,
  action,
  stages,
  className,
}: FunnelBarsProps) {
  const top = stages[0]?.value ?? 0;
  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      action={action}
      className={className}
    >
      <ul className="space-y-2">
        {stages.map((stage, i) => {
          const ofTop = top > 0 ? (stage.value / top) * 100 : 0;
          const prev = i > 0 ? (stages[i - 1]?.value ?? null) : null;
          const stepPct =
            prev != null && prev > 0
              ? Math.round((stage.value / prev) * 100)
              : null;
          return (
            <li key={stage.label}>
              <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                <span className="font-medium text-ink/80">{stage.label}</span>
                <span className="flex items-center gap-2">
                  <span className="font-semibold text-ink">
                    {stage.value.toLocaleString("vi-VN")}
                  </span>
                  {stepPct != null ? (
                    <span className="rounded-full bg-mist px-1.5 py-0.5 text-[10px] font-semibold text-ash">
                      {stepPct}%
                    </span>
                  ) : null}
                </span>
              </div>
              <div className="h-6 overflow-hidden rounded-md bg-mist">
                <div
                  className="flex h-full items-center rounded-md transition-all"
                  style={{
                    width: `${Math.max(ofTop, stage.value > 0 ? 4 : 0)}%`,
                    backgroundColor: CHART_COLORS.primary,
                    opacity: 1 - i * 0.12,
                  }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </ChartCard>
  );
}
